import { Accordion, Box, Button, Indicator, Stack, Title } from "@mantine/core";
import { IconCircleArrowLeft, IconCircleArrowRight } from "@tabler/icons-react";
import { useEffect, useId, useState } from "react";
import type { SiteEnergyWizard, SiteWizard } from "../../utils/backend-types";
import { THEME_VARS } from "../../utils/constants";
import { EnergyWizard } from "./EnergyWizard/EnergyWizard";
import { OnboardingWizard } from "./OnboardingWizard/OnboardingWizard";
import type { OperatorChange } from "./OperatorChanges/OperatorChanges.types";
import { OperatorChangeTaskList } from "./OperatorChanges/OperatorChangeTaskList/OperatorChangeTaskList";
import "./SiteTasks.scss";
import type { TenantChange } from "./TenantChanges/TenantChanges.types";
import { TenantChangeTaskList } from "./TenantChanges/TenantChangeTaskList/TenantChangeTaskList";

type SiteTasksProps = {
  siteWizard: SiteWizard;
  energyWizard: SiteEnergyWizard;
  operatorChanges: Array<OperatorChange>;
  tenantChanges: Array<TenantChange>;
  projectId: string;
  siteIsFullFeedin?: boolean;
  onSiteWizardUpdated: () => Promise<void>;
  onEnergyWizardUpdated: () => void;
  isUpdatingOperatorChanges: boolean;
  isUpdatingTenantChanges: boolean;
  onUpdateOperatorChangeProcess: (
    operatorChange: OperatorChange
  ) => Promise<void>;
  onUpdateTenantChangeProcess: (tenantChange: TenantChange) => Promise<void>;
  onToggle: (isOpen: boolean) => void;
};

function SiteTasks({
  siteWizard,
  energyWizard,
  operatorChanges,
  tenantChanges,
  projectId,
  siteIsFullFeedin,
  onSiteWizardUpdated,
  onEnergyWizardUpdated,
  isUpdatingOperatorChanges,
  isUpdatingTenantChanges,
  onUpdateOperatorChangeProcess,
  onUpdateTenantChangeProcess,
  onToggle
}: SiteTasksProps) {
  const tasksListId = useId();

  const siteWizardFinished = siteIsFullFeedin
    ? siteWizard.phases.msbauthority.done
    : siteWizard.onboardingDone.onboardingDone;
  const siteTasksNotFinished =
    !siteWizardFinished ||
    (energyWizard.enabled && !energyWizard.phases.meters.done);

  const [isOpen, setIsOpen] = useState(siteTasksNotFinished);

  useEffect(() => {
    onToggle(isOpen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleToggle() {
    onToggle(!isOpen);
    setIsOpen(!isOpen);
  }

  const initiallyOpenSections: Array<string> = [];
  if (operatorChanges.length > 0) {
    initiallyOpenSections.push(`operator-change-${operatorChanges[0].id}`);
  }
  if (tenantChanges.length > 0) {
    initiallyOpenSections.push(`tenant-change-${tenantChanges[0].id}`);
  }

  return (
    <Box className="SiteTasks">
      <Button
        aria-controls={tasksListId}
        aria-expanded={isOpen}
        className="header-toggle"
        color="black"
        fz={22}
        leftSection={
          <Indicator color="warning" disabled={!siteTasksNotFinished}>
            {isOpen ? (
              <IconCircleArrowRight
                color={THEME_VARS.primary[6]}
                size="1.4em"
              />
            ) : (
              <IconCircleArrowLeft color={THEME_VARS.primary[6]} size="1.4em" />
            )}
          </Indicator>
        }
        mb="sm"
        p={0}
        variant="transparent"
        onClick={handleToggle}
      >
        <Title
          className={isOpen ? undefined : "header-collapsed"}
          fw={500}
          ms={isOpen ? "sm" : undefined}
          order={3}
        >
          opti.guide
        </Title>
      </Button>

      <Box display={isOpen ? undefined : "none"} mb="md" p={0}>
        <Stack id={tasksListId} pb="xs">
          <OnboardingWizard
            projectId={projectId}
            siteIsFullFeedin={siteIsFullFeedin}
            siteWizard={siteWizard}
            onSiteWizardUpdated={onSiteWizardUpdated}
          />
          {energyWizard.enabled && (
            <EnergyWizard
              energyWizard={energyWizard}
              onEnergyWizardUpdated={onEnergyWizardUpdated}
            />
          )}
          {operatorChanges.length > 0 && (
            <Accordion defaultValue={initiallyOpenSections} multiple>
              <OperatorChangeTaskList
                isUpdating={isUpdatingOperatorChanges}
                operatorChanges={operatorChanges}
                projectId={projectId}
                onUpdateProcess={onUpdateOperatorChangeProcess}
              />
            </Accordion>
          )}
          {tenantChanges.length > 0 && (
            <Accordion defaultValue={initiallyOpenSections} multiple>
              <TenantChangeTaskList
                isUpdating={isUpdatingTenantChanges}
                projectId={projectId}
                tenantChanges={tenantChanges}
                onUpdateProcess={onUpdateTenantChangeProcess}
              />
            </Accordion>
          )}
        </Stack>
      </Box>
    </Box>
  );
}

export { SiteTasks };
